import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../../../api/adminApi';

interface RateLimitsTabProps {
  userId: string;
  userType: 'student' | 'teacher' | 'admin';
}

export function RateLimitsTab({ userId, userType }: RateLimitsTabProps) {
  const { data: rateLimitDetail } = useQuery({
    queryKey: ['rateLimitDetail', userId],
    queryFn: () => adminApi.getRateLimitUserDetail(userId),
    enabled: !!userId,
    refetchInterval: 30000,
  });

  const { data: rateLimitStats } = useQuery({
    queryKey: ['usageStats'],
    queryFn: adminApi.getUsageStats,
    enabled: !!userId,
  });

  const limits = rateLimitStats?.limits?.[userType];
  const submCurrent = rateLimitDetail?.submissionsLastHour ?? 0;
  const llmCurrent = rateLimitDetail?.llmCallsToday ?? 0;
  const submLimit = limits?.submissionsPerHour ?? 60;
  const llmLimit = limits?.llmPerDay ?? 100;
  const apiLimit = limits?.apiPerMinute ?? 60;

  const pctBar = (current: number, limit: number) => {
    const pct = limit > 0 ? (current / limit) * 100 : 0;
    const color = pct > 80 ? '#ef4444' : pct > 60 ? '#eab308' : '#22c55e';
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1, height: 8, background: 'var(--bg-tertiary)', borderRadius: 4, overflow: 'hidden', minWidth: 80 }}>
          <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, background: color, borderRadius: 4, transition: 'width 0.3s' }} />
        </div>
        <span style={{ fontSize: 13, fontWeight: 500, color, minWidth: 48, textAlign: 'right' as const }}>{current}/{limit}</span>
      </div>
    );
  };

  return (
    <div className="features-section">
      <p className="logging-subtitle">
        Tier: <strong style={{ textTransform: 'capitalize' }}>{userType}</strong> &mdash;
        API {apiLimit}/min, Submissions {submLimit}/hr, LLM {llmLimit}/day
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div style={{ background: 'var(--bg-tertiary)', padding: 16, borderRadius: 8 }}>
          <div style={{ fontSize: 13, color: 'var(--text-tertiary)', marginBottom: 4 }}>Submissions (last hour)</div>
          {pctBar(submCurrent, submLimit)}
        </div>
        <div style={{ background: 'var(--bg-tertiary)', padding: 16, borderRadius: 8 }}>
          <div style={{ fontSize: 13, color: 'var(--text-tertiary)', marginBottom: 4 }}>LLM Calls (today)</div>
          {pctBar(llmCurrent, llmLimit)}
        </div>
      </div>

      {rateLimitDetail && rateLimitDetail.hourlyBreakdown.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: 15, margin: '0 0 8px 0', color: 'var(--text-primary)' }}>Hourly Breakdown (Today)</h3>
          <table className="dt dt--compact" style={{ fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ padding: '6px 10px' }}>Hour</th>
                <th style={{ padding: '6px 10px' }}>Submissions</th>
                <th style={{ padding: '6px 10px' }}>LLM Calls</th>
              </tr>
            </thead>
            <tbody>
              {rateLimitDetail.hourlyBreakdown.map(h => (
                <tr key={h.hour}>
                  <td style={{ padding: '6px 10px' }}>{h.hour.substring(11, 16)}</td>
                  <td style={{ padding: '6px 10px' }}>{h.submissions}</td>
                  <td style={{ padding: '6px 10px' }}>{h.llmCalls}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rateLimitDetail && rateLimitDetail.recentEvents.length > 0 && (
        <div>
          <h3 style={{ fontSize: 15, margin: '0 0 8px 0', color: 'var(--text-primary)' }}>Recent Events</h3>
          <table className="dt dt--compact" style={{ fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ padding: '6px 10px' }}>Time</th>
                <th style={{ padding: '6px 10px' }}>Type</th>
                <th style={{ padding: '6px 10px' }}>Operation</th>
                <th style={{ padding: '6px 10px' }}>Provider</th>
              </tr>
            </thead>
            <tbody>
              {rateLimitDetail.recentEvents.slice(0, 20).map(ev => (
                <tr key={ev.id}>
                  <td style={{ padding: '6px 10px' }}>{new Date(ev.createdAt).toLocaleTimeString()}</td>
                  <td style={{ padding: '6px 10px' }}>
                    <span style={{
                      padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 500,
                      background: ev.eventType === 'submission' ? '#dbeafe' : '#fce7f3',
                      color: ev.eventType === 'submission' ? '#1d4ed8' : '#be185d',
                    }}>{ev.eventType}</span>
                  </td>
                  <td style={{ padding: '6px 10px' }}>{ev.operation}</td>
                  <td style={{ padding: '6px 10px' }}>{ev.llmProvider || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
